import { api } from "../../../api/apiSlice";

// Branch-wise master (handoff §9.6) - distinct from
// src/features/users/employeesApi.js, which is the Milestone-2 "link to
// employee" search picker used by Create User (different DTO, different
// permission, different tag - "MasterEmployees" here vs "Employees" there).
//
// Route key switched from {empId} (and briefly {branchId}/{empId}) to
// {recordGuid} server-side 2026-08-12 (Batch48_EmployeeBranchScopeFix.sql) -
// empId collides at real data volume (not even unique within one branch,
// e.g. BranchId=1/empId=1310 matches 5 different real people), RecordGuid is
// the only genuinely unique key. Every GetById/Update/Salaries/Promote call
// below uses recordGuid now; the old {empId} route 404s.
//
// Salary rows are denormalized, one row per deduction applied (confirmed
// live: an employee with 3 deductions has 3 salary rows sharing the same
// amount/allowance/wb/nhif/nssf/pin, differing only by slno and the single
// `deductions` name each row carries) - same full-list diff pattern as
// Deductions' slabs. An employee with zero deductions still needs at least
// one row to persist amount/allowance at all, so the form always sends one
// row minimum (deductions: null when none picked) rather than replicating
// the legacy app's quirk of silently dropping salary data with no deduction.
export const employeesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getEmployees: builder.query({
      query: (params) => ({ url: "/masters/employees", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((e) => ({ type: "MasterEmployees", id: e.recordGuid })),
              { type: "MasterEmployees", id: "LIST" },
            ]
          : [{ type: "MasterEmployees", id: "LIST" }],
    }),
    getEmployee: builder.query({
      query: (recordGuid) => ({ url: `/masters/employees/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "MasterEmployees", id: recordGuid }],
    }),
    createEmployee: builder.mutation({
      query: (body) => ({ url: "/masters/employees", method: "POST", data: body }),
      invalidatesTags: [{ type: "MasterEmployees", id: "LIST" }],
    }),
    updateEmployee: builder.mutation({
      query: ({ recordGuid, ...body }) => ({
        url: `/masters/employees/${recordGuid}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "MasterEmployees", id: recordGuid },
        { type: "MasterEmployees", id: "LIST" },
      ],
    }),
    replaceEmployeeSalaries: builder.mutation({
      query: ({ recordGuid, salaries }) => ({
        url: `/masters/employees/${recordGuid}/salaries`,
        method: "PUT",
        data: { salaries },
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "MasterEmployees", id: recordGuid },
      ],
    }),
    // Re-codes the employee (handoff §9.6, Employees.Promote): deactivates
    // the old record, creates a brand-new one under newEmpCode carrying over
    // every other field, response is the NEW employee's detail - navigate
    // there, not back to the old record. Salary is never carried over
    // (response always has salaries: []) - a separate replaceEmployeeSalaries
    // call against the new recordGuid is needed if the new role's pay differs.
    promoteEmployee: builder.mutation({
      query: ({ recordGuid, ...body }) => ({
        url: `/masters/employees/${recordGuid}/promote`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "MasterEmployees", id: recordGuid },
        { type: "MasterEmployees", id: "LIST" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetEmployeesQuery,
  useGetEmployeeQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useReplaceEmployeeSalariesMutation,
  usePromoteEmployeeMutation,
} = employeesApi;
